import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCompanyStore } from '../store/companyStore';
import { useFeatureStore } from '../store/featureStore';
import api from '../api';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  LayoutDashboard, Users, Clock, Calendar, Gift, CreditCard, FileText, BarChart3,
  LogOut, Menu, Building2, ChevronRight, User, Shield, UserCog, ChevronDown, Check, Wallet,
  TrendingUp, Tag, Layers, Crown, BriefcaseBusiness, Receipt, CheckSquare,
  ClipboardCheck, CalendarDays, ShieldCheck, Briefcase, Award,
  Settings as SettingsIcon,
  Target, Plane, HelpCircle, BookOpen, Sparkles, Cpu, Zap, Laptop, AlertTriangle, GitBranch,
  MapPin, Megaphone, DoorOpen, Code, Book, ArrowLeftRight, HardDrive, Lock,
  ScanFace,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import { SubrivaLogo } from './SubrivaLogo';

// Nav items support an optional `children` array → renders as a collapsible
// group. The parent's `allowedRoles` gates the whole group; each child can
// further restrict via its own `allowedRoles`.
const ALL_NAV = [
  // ── Dashboard (everyone, top-level) ──────────────────────────────────────
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, allowedRoles: [] },
  // ── Approvals inbox — anyone who can act on a workflow step ─────────────
  // RBAC: visible to anyone with at least one approver-style permission.
  { path: '/approvals', label: 'Approvals', icon: ClipboardCheck,
    requiredPermissions: ['leaves.approve', 'advances.manage', 'reimbursements.approve', 'attendance.regularize.approve'],
    allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },

  // ── Platform admin (super_admin only) ────────────────────────────────────
  {
    key: 'platform',
    label: 'Platform',
    icon: Crown,
    allowedRoles: ['super_admin'],
    accent: 'purple',
    children: [
      { path: '/admin/stats', label: 'Overview', icon: TrendingUp, allowedRoles: ['super_admin'] },
      { path: '/companies', label: 'Companies', icon: Building2, allowedRoles: ['super_admin'] },
      { path: '/admin/subscriptions', label: 'Subscriptions', icon: Layers, allowedRoles: ['super_admin'] },
      { path: '/admin/plans', label: 'Plans', icon: CreditCard, allowedRoles: ['super_admin'] },
      { path: '/admin/coupons', label: 'Coupons', icon: Tag, allowedRoles: ['super_admin'] },
    ],
  },

  // ── People (workforce + access management) ───────────────────────────────
  {
    key: 'people',
    label: 'People',
    icon: Users,
    requiredPermissions: ['employees.view', 'users.view', 'lookups.view'],
    allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'],
    children: [
      { path: '/employees', label: 'Employees', icon: Users,
        requiredPermissions: ['employees.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },
      { path: '/users', label: 'User Management', icon: UserCog,
        requiredPermissions: ['users.view'],
        allowedRoles: ['super_admin', 'company_admin'] },
      { path: '/recruitment', label: 'Recruitment', icon: Briefcase,
        requiredPermissions: ['recruitment.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'],
        requiredFeature: 'recruitment' },
      { path: '/performance', label: 'Performance', icon: Award,
        // Managers also see this page (My Reviewing tab) so we include them
        // in allowedRoles even though they lack performance.manage.
        requiredPermissions: ['performance.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager'],
        requiredFeature: 'performance_reviews' },
      { path: '/growth', label: 'Growth · Goals & Kudos', icon: Target,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'employee', 'accountant'] },
      { path: '/org-chart', label: 'Org Chart', icon: GitBranch,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant', 'employee'] },
      { path: '/learning', label: 'Learning', icon: BookOpen,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'employee'],
        requiredFeature: 'lms' },
      { path: '/surveys', label: 'Surveys & eNPS', icon: Sparkles,
        requiredPermissions: ['surveys.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'],
        requiredFeature: 'surveys' },
      { path: '/letters', label: 'Letters', icon: FileText,
        requiredPermissions: ['letters.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/assets', label: 'Assets', icon: Laptop,
        requiredPermissions: ['assets.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/document-expiry', label: 'Doc Expiry', icon: AlertTriangle,
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/directory', label: 'Directory', icon: Book,
        allowedRoles: [] },
      { path: '/resignations', label: 'Exits', icon: DoorOpen,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'employee'] },
      { path: '/talent-pool', label: 'Talent Pool', icon: Users,
        requiredPermissions: ['recruitment.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'],
        requiredFeature: 'recruitment' },
      { path: '/internal-jobs', label: 'Internal Jobs', icon: Briefcase,
        allowedRoles: [] },
      { path: '/skills', label: 'Skills', icon: Award,
        allowedRoles: [] },
      { path: '/my-peer-reviews', label: 'My Peer Reviews', icon: Users,
        allowedRoles: [],
        requiredFeature: 'peer_reviews_360' },
      // Departments & Designations moved into Settings → /settings/departments.
    ],
  },

  // ── Time & Attendance ────────────────────────────────────────────────────
  {
    key: 'time',
    label: 'Time & Attendance',
    icon: Clock,
    allowedRoles: [],   // group visible to all; children further gate
    children: [
      { path: '/attendance', label: 'Attendance', icon: Clock,
        requiredPermissions: ['attendance.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },
      { path: '/my-attendance', label: 'My Attendance', icon: Clock, allowedRoles: ['employee'] }, // self check-in/out
      { path: '/shifts', label: 'Shifts & Roster', icon: CalendarDays,
        requiredPermissions: ['shifts.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager'] },
      { path: '/my-timesheet', label: 'My Timesheet', icon: FileText, allowedRoles: ['employee'] }, // self-log task hours
      { path: '/tasks', label: 'Tasks', icon: CheckSquare,
        requiredPermissions: ['tasks.view_all', 'tasks.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager'] }, // assign tasks
      { path: '/my-tasks', label: 'My Tasks', icon: CheckSquare, allowedRoles: ['employee'] }, // see + update assigned tasks
      { path: '/projects', label: 'Projects', icon: Layers, allowedRoles: [] }, // visible to all; CRUD gated in-page
      { path: '/leaves', label: 'Leaves', icon: Calendar, allowedRoles: [] },   // everyone (employees can apply)
      { path: '/regularizations', label: 'Regularizations', icon: ClipboardCheck, allowedRoles: [] }, // employees raise, managers approve
      // Leave Configuration moved into Settings → /settings/leave-types.
      // Onboarding & Exit moved into Settings → /settings/checklists.
      { path: '/documents', label: 'E-Sign Documents', icon: FileText,
        requiredPermissions: ['documents.signature.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/holidays', label: 'Holiday Calendar', icon: Tag, allowedRoles: [] }, // everyone (read-only for non-HR)
      { path: '/comp-off', label: 'Comp-Off', icon: Calendar,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager'] },
      { path: '/shift-swaps', label: 'Shift Swaps', icon: ArrowLeftRight,
        allowedRoles: [] },
      { path: '/biometric', label: 'Biometric Devices', icon: Cpu,
        requiredPermissions: ['biometric.manage'],
        allowedRoles: ['super_admin', 'company_admin'] },
      { path: '/face-attendance', label: 'Face Attendance', icon: ScanFace,
        requiredPermissions: ['face.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/ot-policies', label: 'OT Policies', icon: Clock,
        requiredPermissions: ['ot.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
    ],
  },

  // ── Payroll & Compensation ───────────────────────────────────────────────
  // Group is visible to admins/HR AND to employees (who only see "My Payslips"
  // inside it — every other child is independently role-gated).
  {
    key: 'payroll',
    label: 'Payroll',
    icon: Wallet,
    allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant', 'employee'],
    children: [
      { path: '/payroll', label: 'Run Payroll', icon: FileText,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },
      { path: '/pay-runs', label: 'Pay Runs (Staged)', icon: FileText,
        requiredPermissions: ['pay_runs.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'] },
      { path: '/reimbursements', label: 'Reimbursements', icon: Receipt,
        requiredPermissions: ['reimbursements.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant', 'employee'] },
      { path: '/tax-declarations', label: 'Tax Declarations', icon: Receipt,
        requiredPermissions: ['tax.verify'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant', 'employee'] },
      { path: '/my-payslips', label: 'My Payslips', icon: Receipt, allowedRoles: ['employee'] },
      { path: '/my-bonus', label: 'My Bonus', icon: Gift, allowedRoles: ['employee'] },
      { path: '/my-advance', label: 'My Advances', icon: CreditCard, allowedRoles: ['employee'],
        requiredFeature: 'advances' },
      { path: '/my-travel', label: 'My Travel', icon: Plane, allowedRoles: ['employee'] },
      { path: '/drive', label: 'Drive', icon: HardDrive,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'employee'],
        requiredFeature: 'drive' },
      { path: '/salary-grades', label: 'Salary Grades', icon: Layers,
        requiredPermissions: ['salary.grades.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      // Salary Components moved into Settings → /settings/salary-components.
      { path: '/bonus', label: 'Bonus & Incentives', icon: Gift,
        requiredPermissions: ['bonus.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'],
        requiredFeature: 'bonus' },
      { path: '/advance', label: 'Advances & Loans', icon: CreditCard,
        requiredPermissions: ['advances.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'],
        requiredFeature: 'advances' },
      { path: '/loans', label: 'Loans', icon: CreditCard,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'] },
      { path: '/salary-revisions', label: 'Salary Revisions', icon: TrendingUp,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/investment-proofs', label: 'Investment Proofs', icon: ShieldCheck,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'] },
      { path: '/travel', label: 'Travel & Expense', icon: Plane,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant', 'employee'] },
      { path: '/reimbursement-categories', label: 'Reimb. Categories', icon: Receipt,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'] },
      { path: '/salary-templates', label: 'Salary Templates', icon: Layers,
        requiredPermissions: ['salary.templates.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/fbp', label: 'FBP (Flexi Benefits)', icon: Wallet,
        requiredPermissions: ['salary.templates.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/my-fbp', label: 'My FBP', icon: Wallet,
        allowedRoles: ['employee'] },
      { path: '/accounting-export', label: 'Accounting Export', icon: FileText,
        requiredPermissions: ['payroll.export'],
        allowedRoles: ['super_admin', 'company_admin', 'accountant'] },
    ],
  },

  // ── Helpdesk (everyone can raise; HR resolves) ───────────────────────────
  { path: '/helpdesk', label: 'Helpdesk', icon: HelpCircle, allowedRoles: [] },

  // ── Insights ─────────────────────────────────────────────────────────────
  {
    key: 'insights',
    label: 'Insights',
    icon: TrendingUp,
    requiredPermissions: ['payroll.view', 'employees.view'],
    allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'],
    children: [
      { path: '/analytics', label: 'Analytics', icon: BarChart3,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },
      { path: '/reports', label: 'Reports', icon: FileText,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'accountant'] },
      { path: '/ai-insights', label: 'AI Insights', icon: BarChart3,
        requiredPermissions: ['payroll.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant'],
        requiredFeature: 'ai_insights' },
      { path: '/report-builder', label: 'Report Builder', icon: BarChart3,
        requiredPermissions: ['reports.build'],
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'accountant', 'manager'],
        requiredFeature: 'report_builder' },
    ],
  },

  // ── Administration (org-level admin tools) ───────────────────────────────
  {
    key: 'admin',
    label: 'Administration',
    icon: BriefcaseBusiness,
    // Settings is the catch-all for config (it owns roles, approval chains,
    // lookups, leave types, salary components, checklists internally via the
    // SettingsLayout shell). Only operational admin pages stay top-level.
    requiredPermissions: ['company.billing.manage', 'company.settings.update', 'company.audit.view'],
    allowedRoles: ['super_admin', 'company_admin', 'hr'],
    children: [
      { path: '/settings', label: 'Settings', icon: SettingsIcon,
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/billing', label: 'Billing', icon: Receipt,
        requiredPermissions: ['company.billing.manage'],
        allowedRoles: ['super_admin', 'company_admin'] },
      { path: '/audit-logs', label: 'Audit Logs', icon: Shield,
        requiredPermissions: ['company.audit.view'],
        allowedRoles: ['super_admin', 'company_admin'],
        requiredFeature: 'audit_logs' },
      { path: '/workflows', label: 'Workflow Automation', icon: Zap,
        requiredPermissions: ['workflows.manage'],
        allowedRoles: ['super_admin', 'company_admin'],
        requiredFeature: 'workflows' },
      { path: '/locations', label: 'Locations', icon: MapPin,
        requiredPermissions: ['locations.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'],
        requiredFeature: 'multi_branch' },
      { path: '/custom-fields', label: 'Custom Fields', icon: Layers,
        requiredPermissions: ['custom_fields.manage'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'] },
      { path: '/webhooks', label: 'Webhooks & API', icon: Code,
        requiredPermissions: ['webhooks.manage'],
        allowedRoles: ['super_admin', 'company_admin'] },
      { path: '/plugins', label: 'Plugin Marketplace', icon: Code,
        requiredPermissions: ['plugins.view'],
        allowedRoles: ['super_admin', 'company_admin', 'hr'],
        requiredFeature: 'plugins' },
      { path: '/announcements', label: 'Announcements', icon: Megaphone,
        allowedRoles: ['super_admin', 'company_admin', 'hr', 'manager', 'employee'] },
    ],
  },

  // ── Personal account (everyone) ──────────────────────────────────────────
  { path: '/my-profile', label: 'My Profile', icon: User, allowedRoles: ['employee'] },
  { path: '/my-documents', label: 'My Documents', icon: FileText, allowedRoles: ['employee'] },
  { path: '/my-signatures', label: 'My Signatures', icon: ShieldCheck, allowedRoles: ['employee'] },
  { path: '/my-reviews', label: 'My Reviews', icon: Award, allowedRoles: ['employee'] },
  { path: '/account/security', label: 'Security', icon: Shield, allowedRoles: [] },
];

// Flat list of every nav item (top-level + group children) — precomputed once
// since ALL_NAV is a module constant. Used to resolve the header breadcrumb
// label without walking the tree on every render.
const FLAT_NAV = ALL_NAV.flatMap((i) => i.children ? [i, ...i.children] : [i]);
const PATH_LABELS = Object.fromEntries(
  FLAT_NAV.filter((n) => n.path).map((n) => [n.path, n.label])
);

// Stable data-testid from a nav path: drop leading slash, replace remaining
// slashes with dashes. Keeps `/dashboard` → `nav-dashboard` and
// `/admin/stats` → `nav-admin-stats` (no leading dash).
const navTestId = (path) => `nav-${path.replace(/^\//, '').replace(/\//g, '-')}`;

export default function Layout() {
  const user = useAuthStore(s => s.user);
  const clearAuth = useAuthStore(s => s.clear);
  const { activeCompany, companies, setActiveCompany, setCompanies, clearCompany } = useCompanyStore();
  const featureFlags = useFeatureStore((s) => s.flags);
  const featuresLoaded = useFeatureStore((s) => s.loaded);
  const nextPlanName = useFeatureStore((s) => s.nextPlanName);
  const fetchFeatures = useFeatureStore((s) => s.fetch);
  const clearFeatures = useFeatureStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  // Auto-collapse on tablets/small laptops so the 256px rail doesn't eat
  // the viewport. Initial value is computed synchronously to avoid a flash
  // of the expanded sidebar on first paint on narrow screens.
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  // Drawer mode for true-mobile / portrait-tablet (<768px): sidebar slides in
  // over the page instead of stealing horizontal space. Closed by default.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [companyDropOpen, setCompanyDropOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';
  const role = user?.role || '';
  // RBAC: user.permissions is a flat array of permission keys, populated by
  // /auth/me + login response. For users on a stale frontend session it may
  // be missing — we tolerate that and fall back to allowedRoles below.
  // Set so each nav-item check is O(1) instead of O(perms) across ~20 items.
  const userPerms = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);

  // Visibility rules: an item is visible if EITHER gate passes (OR semantics).
  //   • requiredPermissions  → RBAC-aware path; custom roles work automatically.
  //   • allowedRoles         → legacy enum match; covers self-service items
  //                            (/my-attendance, etc.) AND acts as a fallback
  //                            when user.permissions hasn't loaded yet (stale
  //                            persisted session, transient /auth/me failure).
  //   • Neither set          → visible to everyone.
  // Per-item lock check. Feature lock is independent of role/permission
  // visibility — a user CAN see the item exists but cannot enter it. Locked
  // rows render with a Lock icon and route to /billing on click.
  const isLocked = (item) => {
    if (!item.requiredFeature) return false;
    if (!featuresLoaded) return false;
    if (isSuperAdmin) return false;
    return featureFlags[item.requiredFeature] === false;
  };

  const visibleNav = useMemo(() => {
    const canSee = (item) => {
      const hasPermGate = item.requiredPermissions && item.requiredPermissions.length > 0;
      const hasRoleGate = item.allowedRoles && item.allowedRoles.length > 0;
      if (!hasPermGate && !hasRoleGate) return true;
      if (hasPermGate && item.requiredPermissions.some((p) => userPerms.has(p))) return true;
      if (hasRoleGate && item.allowedRoles.includes(role)) return true;
      return false;
    };
    return ALL_NAV
      .filter(canSee)
      .map((item) => item.children
        ? { ...item, children: item.children.filter(canSee) }
        : item)
      .filter((item) => !item.children || item.children.length > 0);
  }, [userPerms, role]);

  // Click handler factory — routes to /billing instead of the page when the
  // feature isn't on the current plan. Otherwise normal navigate().
  const handleNavClick = (item) => {
    if (isLocked(item)) {
      navigate('/billing');
      return;
    }
    navigate(item.path);
  };

  // Track which groups are expanded. Auto-open the group that contains the
  // current route, plus remember the user's manual toggles in this session.
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    for (const item of ALL_NAV) {
      if (item.children?.some((c) => c.path === location.pathname)) initial[item.key] = true;
    }
    return initial;
  });
  useEffect(() => {
    for (const item of ALL_NAV) {
      if (item.children?.some((c) => c.path === location.pathname)) {
        setOpenGroups((prev) => prev[item.key] ? prev : { ...prev, [item.key]: true });
      }
    }
    // Auto-close the mobile drawer when the user navigates to a new page.
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Keep `collapsed` in sync with viewport: collapse on tablet (<1024px),
  // expand again on desktop. Doesn't fight the user's explicit toggle within
  // a single breakpoint — only flips on the actual breakpoint crossing.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e) => setCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // Authenticated shell owns the viewport: `main` is the only scroll
   // container. Tag <body> so index.css can lock html/body/#root overflow
   // and prevent the browser from rendering a *second* scrollbar next to
   // main's. Removed on unmount so public/auth pages keep document scroll.
  useEffect(() => {
    document.body.classList.add('app-shell');
    return () => document.body.classList.remove('app-shell');
  }, []);

  // Fetch the tenant's plan feature_flags after auth settles. Re-runs when
  // the active company changes (super_admin switching tenants).
  useEffect(() => {
    if (!user) {
      clearFeatures();
      return;
    }
    fetchFeatures();
  }, [user?.id, activeCompany?.id, fetchFeatures, clearFeatures]);

  // Always re-fetch companies list for super_admin on mount so the switcher stays up-to-date.
  // Intentionally only depends on isSuperAdmin — re-running on activeCompany changes would
  // cause an infinite re-fetch loop since this effect can also call setActiveCompany.
  // setCompanies / setActiveCompany are stable zustand setters (not closures over render state).
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/companies').then(({ data }) => {
      setCompanies(data);
      // If no company selected yet (first login), auto-select the first one
      if (!activeCompany && data.length > 0) {
        setActiveCompany(data[0]);
      }
    }).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // ignore — we clear local state regardless
    }
    clearCompany();
    clearAuth();
    setLoggingOut(false);
    setLogoutConfirmOpen(false);
    navigate('/login');
  };

  // Everything under /settings/* falls through to "Settings" — the SettingsLayout
  // rail already tells the user which sub-section is active, so we don't need
  // to mirror it in the header.
  const currentLabel = PATH_LABELS[location.pathname]
    || (location.pathname.startsWith('/settings') ? 'Settings' : 'Dashboard');

  return (
    <div className="flex h-viewport app-bg overflow-hidden">
      {/* Mobile drawer scrim — only shown when drawer is open on <md screens.
          Click closes the drawer. Hidden on md+ where sidebar is in-flow. */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar — in-flow on md+, slide-in overlay on mobile.
          On <md it's `fixed` so it doesn't reserve horizontal space, and
          translates off-screen when `mobileNavOpen` is false. */}
      <aside
        className={`${collapsed ? 'w-16' : 'w-64'} max-w-[85vw] md:max-w-none flex flex-col transition-all duration-200 flex-shrink-0 border-r border-slate-200/70 backdrop-blur-sm
          fixed inset-y-0 left-0 z-50 md:static md:translate-x-0
          ${mobileNavOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0'}`}
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 60%, #f1f5ff 100%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200/70">
          <SubrivaLogo size={36} className="flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-bold gradient-text truncate" style={{ fontFamily: 'Manrope' }}>Subriva</p>
              <p className="text-xs truncate">
                {isSuperAdmin
                  ? <span className="text-violet-600 font-semibold">Super Admin</span>
                  : <span className="text-slate-500">{user?.company?.name || 'HR & Payroll'}</span>
                }
              </p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {visibleNav.map((item) => {
            // ── Group (has children) ─────────────────────────────────────────
            if (item.children) {
              const GroupIcon = item.icon;
              const isOpen = openGroups[item.key];
              const hasActiveChild = item.children.some((c) => c.path === location.pathname);
              const isPurple = item.accent === 'purple';

              // Collapsed sidebar: just render children flat as icons, no group header.
              if (collapsed) {
                return (
                  <React.Fragment key={item.key}>
                    {item.children.map((child) => {
                      const { path, label, icon: Icon } = child;
                      const active = location.pathname === path;
                      const locked = isLocked(child);
                      return (
                        <button
                          key={path}
                          onClick={() => handleNavClick(child)}
                          className={`sidebar-link w-full relative ${active ? 'active' : ''}`}
                          title={locked ? `${label} — upgrade${nextPlanName ? ` to ${nextPlanName}` : ''} to unlock` : label}
                        >
                          <Icon size={18} strokeWidth={1.5} className={`flex-shrink-0 ${isPurple ? 'text-purple-500' : ''} ${locked ? 'opacity-50' : ''}`} />
                          {locked && (
                            <Lock size={9} className="absolute right-1.5 top-1.5 text-amber-500 stroke-[2.5]" />
                          )}
                        </button>
                      );
                    })}
                  </React.Fragment>
                );
              }

              return (
                <div key={item.key} className="space-y-0.5">
                  <button
                    onClick={() => toggleGroup(item.key)}
                    data-testid={`nav-group-${item.key}`}
                    className={`sidebar-link w-full ${hasActiveChild && !isOpen ? 'active' : ''}`}
                  >
                    <GroupIcon size={18} strokeWidth={1.5} className={`flex-shrink-0 ${isPurple ? 'text-purple-500' : ''}`} />
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      size={14}
                      className={`ml-auto text-slate-400 transition-transform duration-150 ${isOpen ? '' : '-rotate-90'}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="ml-3 pl-2 border-l border-slate-200 space-y-0.5">
                      {item.children.map((child) => {
                        const { path, label, icon: Icon } = child;
                        const active = location.pathname === path;
                        const locked = isLocked(child);
                        return (
                          <button
                            key={path}
                            onClick={() => handleNavClick(child)}
                            data-testid={navTestId(path)}
                            title={locked ? `Upgrade${nextPlanName ? ` to ${nextPlanName}` : ''} to unlock ${label}` : undefined}
                            className={`sidebar-link w-full text-sm ${active ? 'active' : ''} ${locked ? 'text-slate-400 hover:text-slate-600' : ''}`}
                          >
                            <Icon size={16} strokeWidth={1.5} className={`flex-shrink-0 ${isPurple ? 'text-purple-500' : ''} ${locked ? 'opacity-60' : ''}`} />
                            <span className="truncate">{label}</span>
                            {locked && <Lock size={12} className="ml-auto text-amber-500 flex-shrink-0" />}
                            {active && !locked && <ChevronRight size={14} className="ml-auto text-indigo-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // ── Leaf item ────────────────────────────────────────────────────
            const { path, label, icon: Icon } = item;
            const active = location.pathname === path;
            const locked = isLocked(item);
            const tooltip = locked
              ? `Upgrade${nextPlanName ? ` to ${nextPlanName}` : ''} to unlock ${label}`
              : (collapsed ? label : undefined);
            return (
              <button
                key={path}
                onClick={() => handleNavClick(item)}
                data-testid={navTestId(path)}
                title={tooltip}
                className={`sidebar-link w-full ${active ? 'active' : ''} ${locked ? 'text-slate-400 hover:text-slate-600' : ''}`}
              >
                <Icon size={18} strokeWidth={1.5} className={`flex-shrink-0 ${locked ? 'opacity-60' : ''}`} />
                {!collapsed && <span className="truncate">{label}</span>}
                {!collapsed && locked && <Lock size={12} className="ml-auto text-amber-500 flex-shrink-0" />}
                {!collapsed && active && !locked && <ChevronRight size={14} className="ml-auto text-indigo-400" />}
              </button>
            );
          })}
        </nav>

        {/* User & Logout — the safe-area inset keeps the logout button clear
            of the iOS home indicator / Android gesture bar in the drawer. */}
        <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-slate-200/70 space-y-1 flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-100/60"
                 style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                   style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' }}>
                <User size={14} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          )}
          <button onClick={() => setLogoutConfirmOpen(true)} data-testid="logout-btn" className="sidebar-link w-full justify-center !text-rose-600 hover:!bg-gradient-to-r hover:!from-rose-50 hover:!to-pink-50 hover:!text-rose-700">
            <LogOut size={18} strokeWidth={1.5} />
            {!collapsed && <span>Logout</span>}
          </button>

          {/* Powered by — links to the parent company site in a new tab.
              rel="noreferrer" protects against tabnabbing. */}
          {!collapsed && (
            <div className="pt-2 mt-1 border-t border-slate-200/70">
              <a
                href="https://subrivatech.com"
                target="_blank"
                rel="noreferrer"
                className="block text-[10px] text-center text-slate-400 leading-tight hover:text-slate-600 transition-colors group"
              >
                Powered by{' '}
                <span className="font-semibold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent group-hover:from-indigo-600 group-hover:to-violet-600">
                  Subriva Tech
                </span>
              </a>
            </div>
          )}
        </div>
      </aside>

      {/* Logout confirmation */}
      <AlertDialog open={logoutConfirmOpen} onOpenChange={(o) => !loggingOut && setLogoutConfirmOpen(o)}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-rose-50 to-pink-100 ring-1 ring-rose-200/60">
                <LogOut size={18} className="text-rose-600" />
              </div>
              <div>
                <AlertDialogTitle>Log out of Subriva HR &amp; Payroll?</AlertDialogTitle>
                <AlertDialogDescription className="mt-1">
                  You'll need to sign in again to access your account.
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm-btn"
              disabled={loggingOut}
              onClick={(e) => { e.preventDefault(); handleLogout(); }}
              className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
            >
              {loggingOut ? 'Logging out…' : 'Log out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header — glassy. relative+z-50 so dropdowns inside it
            (notifications, company switcher) sit above the scrolling main
            content, whose cards create their own stacking contexts. */}
        <header className="glass relative z-50 border-b border-slate-200/70 px-3 sm:px-4 md:px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={() => {
                // On mobile the sidebar is a slide-in drawer; on md+ it's
                // the in-flow rail that toggles between full / icon width.
                if (window.matchMedia('(max-width: 767px)').matches) {
                  // Drawer always shows full labels — expand if collapsed.
                  setMobileNavOpen((o) => {
                    if (!o) setCollapsed(false);
                    return !o;
                  });
                } else {
                  setCollapsed(!collapsed);
                }
              }}
              className="p-1.5 rounded-lg hover:bg-gradient-to-br hover:from-indigo-50 hover:to-violet-50 transition-colors flex-shrink-0"
              aria-label="Toggle navigation"
            >
              <Menu size={18} className="text-slate-600" />
            </button>
            <div className="text-sm font-medium text-slate-700 truncate">{currentLabel}</div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* ── Super Admin Company Switcher ── */}
            {isSuperAdmin && (
              <div className="relative">
                <button
                  onClick={() => setCompanyDropOpen(o => !o)}
                  className="flex items-center gap-2 h-8 px-3 rounded-lg border border-violet-200/60 transition-all text-sm font-semibold text-violet-700 shadow-sm hover:shadow hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #fdf4ff 100%)' }}
                >
                  <Building2 size={14} className="text-violet-500 flex-shrink-0" />
                  <span className="max-w-[90px] sm:max-w-[160px] truncate">
                    {activeCompany ? activeCompany.name : 'Select Company'}
                  </span>
                  <ChevronDown size={13} className="text-violet-400 flex-shrink-0" />
                </button>

                {/* Dropdown */}
                {companyDropOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setCompanyDropOpen(false)} />
                    <div className="absolute right-0 top-10 z-20 w-64 rounded-xl overflow-hidden border border-slate-200/70 shadow-[0_24px_48px_-12px_rgba(99,102,241,0.25)]"
                         style={{ background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 100%)' }}>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-3 pt-3 pb-1.5">
                        Viewing Company As
                      </p>
                      <div className="max-h-60 overflow-y-auto">
                        {companies.map(c => (
                          <button
                            key={c.id}
                            onClick={() => { setActiveCompany(c); setCompanyDropOpen(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 transition-colors text-left hover:bg-gradient-to-r hover:from-indigo-50 hover:to-violet-50"
                          >
                            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 shadow-sm"
                                 style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' }}>
                              <Building2 size={13} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                              {c.gst_number && <p className="text-[10px] text-slate-400 mono truncate">{c.gst_number}</p>}
                            </div>
                            {activeCompany?.id === c.id && (
                              <Check size={14} className="text-indigo-600 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                        {companies.length === 0 && (
                          <p className="px-3 py-4 text-sm text-slate-400 text-center">No companies found</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Company name badge for non-super-admin. Hidden on phones — the
                name already sits in the sidebar header, so it yields the space
                to the notification bell. */}
            {!isSuperAdmin && user?.company?.name && (
              <span className="hidden sm:flex items-center gap-1.5 max-w-[180px] text-xs font-semibold text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100"
                    style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)' }}>
                <Building2 size={11} className="text-indigo-400 flex-shrink-0" />
                <span className="truncate">{user.company.name}</span>
              </span>
            )}

            <NotificationBell />
            <div className="hidden sm:block text-xs text-slate-600 font-mono px-2.5 py-1 rounded-lg border border-slate-200/70 shadow-sm whitespace-nowrap"
                 style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)' }}>
              {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </header>

        {/* Page Content — overflow-auto (both axes) so wide tables/grids
            get a horizontal scrollbar instead of being clipped. Tighter
            padding on small screens so pages don't waste space on tablet. */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-[calc(1.5rem+env(safe-area-inset-bottom))] animate-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
